import { useCallback, useEffect, useRef, useState } from "react";
import type { OpenClaw } from "../types/openclaw";
import { getCsrfHeader } from "../lib/csrf";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";

export type ClawsStatus = "loading" | "ready" | "error";

export type DeleteErrorCode = "NOT_FOUND" | "INVALID_STATE" | "UNAUTHORIZED" | "UNKNOWN";

export type DeleteResult =
  | { ok: true }
  | { ok: false; code: DeleteErrorCode; message: string };

export type ReassignErrorCode = "CAP" | "DUPLICATE" | "NOT_FOUND" | "INVALID" | "UNAUTHORIZED" | "UNKNOWN";

export type ReassignResult =
  | { ok: true; claw: OpenClaw }
  | { ok: false; code: ReassignErrorCode; message: string };

export type UseClawsResult = {
  status: ClawsStatus;
  claws: ReadonlyArray<OpenClaw>;
  error: string | null;
  deleteClaw: (tenantId: string) => Promise<DeleteResult>;
  reassign: (tenantId: string, ownerEmail: string) => Promise<ReassignResult>;
};

const STREAM_URL = "/api/claws/stream";
const LIST_URL = "/api/claws";
const ADMIN_REASSIGN_URL = "/api/admin/claws";
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

type ServerError = { code: string; message: string };

const isDeleteErrorCode = (code: string): code is DeleteErrorCode =>
  code === "NOT_FOUND" || code === "INVALID_STATE" || code === "UNAUTHORIZED" || code === "UNKNOWN";

const isReassignErrorCode = (code: string): code is ReassignErrorCode =>
  code === "CAP" || code === "DUPLICATE" || code === "NOT_FOUND" ||
  code === "INVALID" || code === "UNAUTHORIZED" || code === "UNKNOWN";

const CLAWS_RESOURCE = "claws";

// Seeds the cached list with an instance the caller just created, so the
// dashboard it lands on shows it straight away instead of the cached
// "launch your first OpenClaw" state until the stream catches up.
export const rememberNewClaw = (claw: OpenClaw): void => {
  const cached = readUserCache<OpenClaw[]>(CLAWS_RESOURCE) ?? [];
  writeUserCache(CLAWS_RESOURCE, [...cached.filter((c) => c.tenantId !== claw.tenantId), claw]);
};

export const useClaws = (): UseClawsResult => {
  const [cached] = useState<ReadonlyArray<OpenClaw> | null>(() =>
    readUserCache<OpenClaw[]>(CLAWS_RESOURCE),
  );
  const [status, setStatus] = useState<ClawsStatus>(cached !== null ? "ready" : "loading");
  const [claws, setClaws] = useState<ReadonlyArray<OpenClaw>>(cached ?? []);
  const [error, setError] = useState<string | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const backoffRef = useRef<number>(RECONNECT_MIN_MS);

  useEffect(() => {
    let cancelled = false;

    const connect = (): void => {
      if (cancelled) return;
      const src = new EventSource(STREAM_URL, { withCredentials: true });
      sourceRef.current = src;

      src.addEventListener("snapshot", (evt) => {
        if (cancelled) return;
        try {
          const parsed = JSON.parse((evt as MessageEvent).data) as OpenClaw[];
          setClaws(parsed);
          setStatus("ready");
          setError(null);
          backoffRef.current = RECONNECT_MIN_MS;
          writeUserCache(CLAWS_RESOURCE, parsed);
        } catch (err) {
          console.error("[useClaws] snapshot parse failed", err);
        }
      });

      src.onerror = () => {
        if (cancelled) return;
        src.close();
        sourceRef.current = null;
        setStatus((prev) => (prev === "ready" ? "ready" : "error"));
        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
        window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, []);

  const deleteClaw = useCallback(async (tenantId: string): Promise<DeleteResult> => {
    try {
      const res = await fetch(`${LIST_URL}/${encodeURIComponent(tenantId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });

      if (res.status === 204) {
        setClaws((prev) => {
          const next = prev.filter((c) => c.tenantId !== tenantId);
          writeUserCache(CLAWS_RESOURCE, next);
          return next;
        });
        return { ok: true };
      }

      if (res.status === 401) {
        return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
      }

      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isDeleteErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Delete failed (${res.status})` };
      }

      return { ok: false, code: "UNKNOWN", message: `Delete failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const reassign = useCallback(async (tenantId: string, ownerEmail: string): Promise<ReassignResult> => {
    try {
      const res = await fetch(
        `${ADMIN_REASSIGN_URL}/${encodeURIComponent(tenantId)}/reassign`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...getCsrfHeader(),
          },
          body: JSON.stringify({ ownerEmail }),
        },
      );

      if (res.status === 200) {
        const claw = (await res.json()) as OpenClaw;
        // Drop the tenant from the admin's local snapshot immediately; the
        // SSE stream will confirm on its next tick.
        setClaws((prev) => {
          const next = prev.filter((c) => c.tenantId !== claw.tenantId);
          writeUserCache(CLAWS_RESOURCE, next);
          return next;
        });
        return { ok: true, claw };
      }

      if (res.status === 401) {
        return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
      }

      if (res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }

      if (res.status === 400 || res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isReassignErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Reassign failed (${res.status})` };
      }

      return { ok: false, code: "UNKNOWN", message: `Reassign failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  return { status, claws, error, deleteClaw, reassign };
};
